import { RefundPreviewResponse, ServiceFeeBorneBy, serviceFeeBorneByPhrase } from '../../types/refunds';

interface ServiceFeeRefundCheckboxProps {
  /** VAT-inclusive service fee charged on the payment. Nothing renders when it is null/0.00. */
  feeCharged: number | null | undefined;
  currency: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Who bears the fee if it is refunded (from the preview); drives the explanation line. */
  borneBy?: ServiceFeeBorneBy | null;
}

/**
 * The checkbox state: the admin's explicit choice, else billing's policy default for the fault party.
 * A preview requested without `refundServiceFee` returns that default, so `serviceFeeRefunded > 0`
 * IS the default (Spec 068 AC-18: PLATFORM/GARAGE ticked, CUSTOMER/NONE unticked).
 */
export function serviceFeeRefundChecked(explicit: boolean | undefined, preview: RefundPreviewResponse | null): boolean {
  if (explicit !== undefined) return explicit;
  return (preview?.serviceFeeRefunded ?? 0) > 0;
}

/**
 * Spec 068 V11 / AC-18 — "Refund service fee (AED 35.00)", beside the delivery-fee checkbox.
 *
 * <p>Shown only when the payment carries a fee. The fee is Garagat's own supply: refunding it issues
 * a credit note against Garagat's invoice (PCN). Who pays for that refund follows the fault party —
 * Garagat when it was at fault, the garage (as a "Service fee recharge" at settlement) when the
 * garage was — and the line under the label says which, so the admin never has to infer it.
 */
export default function ServiceFeeRefundCheckbox({
  feeCharged,
  currency,
  checked,
  onChange,
  disabled,
  borneBy,
}: ServiceFeeRefundCheckboxProps) {
  if (feeCharged == null || feeCharged <= 0) return null;

  const bearer = serviceFeeBorneByPhrase(borneBy);
  const explanation = !checked
    ? 'Garagat keeps its service fee; the customer is not refunded for it.'
    : borneBy === 'GARAGE'
      ? 'The garage was at fault: the customer gets the fee back and it is recharged to the garage at settlement (Service fee recharge).'
      : bearer
        ? `Refunded to the customer, ${bearer}. Issues a credit note against Garagat's invoice.`
        : "Refunded to the customer. Issues a credit note against Garagat's invoice.";

  return (
    <div className="admin-form-group" data-testid="refund-service-fee-field">
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-describedby="refund-service-fee-help"
        />
        Refund service fee (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {currency} {feeCharged.toFixed(2)}
        </span>
        )
      </label>
      <p id="refund-service-fee-help" style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', margin: '0.25rem 0 0', marginInlineStart: '1.5rem' }}>
        {explanation}
      </p>
    </div>
  );
}
